import React, { useState } from 'react';
import { Button, TextArea, Autocomplete } from '@hevoio/hevo-ui-toolkit';
import styles from './IssueTypeContent.module.scss';
import clsx from 'clsx';
import { OnGetSolutionHandler } from '../../interfaces/query';
import { Option, ENTITY_TYPES, ISSUE_CATEGORIES_BY_ENTITY } from '../../constants/IssueType';

interface FormState {
  entityType: Option;
  category: Option;
  issueDescription: string;
}

export default function IssueTypeContent({
  onGetSolution
}: {
  onGetSolution: OnGetSolutionHandler;
}) {
  const [formState, setFormState] = useState<FormState>({
    entityType: ENTITY_TYPES[0],
    category: ISSUE_CATEGORIES_BY_ENTITY[ENTITY_TYPES[0].value][0],
    issueDescription: ''
  });
  const [issueError, setIssueError] = useState('');

  const handleOptionChange = (key: keyof Pick<FormState, 'entityType' | 'category'>) => {
    return (_: unknown, v: Option) => {
      setFormState(prev => ({
        ...prev,
        [key]: v,
        ['category']: key === 'entityType' ? ISSUE_CATEGORIES_BY_ENTITY[v.value][0] : v
      }));
    };
  };

  const handleDescriptionChange = (value: string) => {
    setFormState(prev => ({ ...prev, issueDescription: value }));
    setIssueError('');
  };

  const handleGetSolution = () => {
    if (
      !formState.entityType.value ||
      !formState.category.value ||
      !formState.issueDescription.trim()
    ) {
      setIssueError(formState.issueDescription.trim() ? '' : 'Issue description is required');
      return;
    }

    onGetSolution({
      entityType: formState.entityType.value,
      category: formState.category.value,
      issueDescription: formState.issueDescription
    });
  };

  return (
    <div className={styles.issueTypeContent}>
      <div className={styles.formField}>
        <Autocomplete
          dataId='entity-type-autocomplete'
          textFieldProps={{
            label: 'Entity Type',
            placeholder: 'Select entity type'
          }}
          options={ENTITY_TYPES}
          value={formState.entityType}
          disableClearable
          onChange={handleOptionChange('entityType')}
        />
      </div>

      <div className={styles.formField}>
        <Autocomplete
          dataId='category-autocomplete'
          textFieldProps={{
            label: 'Category',
            placeholder: 'Select category'
          }}
          options={ISSUE_CATEGORIES_BY_ENTITY[formState.entityType.value]}
          value={formState.category}
          disableClearable
          onChange={handleOptionChange('category')}
        />
      </div>

      <div className={styles.formField}>
        <label className={`text-label-small ${styles.label}`}>Explain your issue*</label>
        <TextArea
          value={formState.issueDescription}
          onChangeHandler={handleDescriptionChange}
          className={clsx(styles.textArea, issueError && 'error-border')}
          placeholder={`I have ${
            formState.category.label === 'Others' ? 'other' : formState.category.label
          } issue with my ${formState.entityType.label} ...`}
        />
        
        {issueError ? <div className={'text-caption-1 text-error'}>{issueError}*</div> : null }
      </div>

      <div className={styles.buttonContainer}>
        <Button className='text-transform-none' onClick={() => handleGetSolution()}>Find a solution</Button>
      </div>
    </div>
  );
}
